/**
 * Building render manager — owns the per-building Object3D map; reconciles
 * additions/removals from the schema's buildings map; updates terrain anchor
 * height and translucent-while-constructing visual each frame.
 *
 * Mirrors the creature-render pattern (single Object3D per entity, reconcile
 * against an Iterable<[id, schema]>). Each root carries `userData = { id, type }`
 * so `pickBuilding` can recover the schema id from a raycast hit on a child mesh.
 *
 * All 12 buildings ship with FBX placeholders — each instance clones the
 * cached `THREE.Group`. The primitive-fallback path (boxes/cones sized from
 * footprint) stays in place as a safety net if an FBX fails to load or a
 * future building registers with `meshUrl: null`. Primitives use
 * `MeshLambertMaterial` to match the Quaternius vertex-color flat-shaded
 * look used by `world.ts`.
 */

import * as THREE from "three";
import { BUILDING_DEFS, type Building, type BuildingType } from "@tribez/shared";
import type { AssetBundle } from "../assets";
import { applyToonShader, bakeBaseAO } from "../toon";

// Per `docs/design/14-rendering.md` §Materials: only player characters get toon
// promotion. Buildings stay on Lambert so they share the world's continuous
// gradient — toon's 30% shadow band turned dark FBX meshes into near-black
// silhouettes when their lit face pointed away from the sun.
function toLambertWithRim(src: THREE.Material, noiseScale: number): THREE.MeshLambertMaterial {
  const s = src as THREE.MeshStandardMaterial;
  const out = new THREE.MeshLambertMaterial({
    map: s.map,
    color: s.color,
    vertexColors: s.vertexColors,
    transparent: s.transparent,
    opacity: s.opacity,
    alphaTest: s.alphaTest,
    side: s.side,
  });
  out.name = s.name;
  out.alphaMap = s.alphaMap;
  applyToonShader(out, { noiseScale });
  return out;
}

export interface BuildingRenderManager {
  group: THREE.Group;
  reconcile(entries: Iterable<[string, Building]>): void;
  pickBuilding(raycaster: THREE.Raycaster): string | null;
  update(dt: number): void;
}

// Lazily-built radial-gradient sprite texture for the campfire's warm halo.
// One texture shared across every campfire (cloned material per instance).
let glowTex: THREE.Texture | null = null;
function campfireGlowTexture(): THREE.Texture {
  if (glowTex) return glowTex;
  const size = 64;
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, "rgba(255,210,140,0.95)");
  g.addColorStop(0.4, "rgba(255,150,60,0.5)");
  g.addColorStop(1, "rgba(255,120,40,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  glowTex = new THREE.CanvasTexture(c);
  return glowTex;
}

interface CampfireFlame {
  group: THREE.Group;
  cones: THREE.Mesh[];
  sprite: THREE.Sprite;
  light: THREE.PointLight;
}

// Base point-light intensity; update() flickers around this. Localized (short
// `distance`) so a handful of campfires stay cheap in the forward renderer.
const CAMPFIRE_LIGHT_INTENSITY = 7;

// Stacked additive flame cones (outer orange → inner yellow) + a warm halo sprite
// + a short-range point light. Cones use MeshBasicMaterial so they self-glow at
// night regardless of the sun, same trick as the loot beacon / cast VFX.
function makeCampfireFlame(): CampfireFlame {
  const group = new THREE.Group();
  // Sit the flame just above the log bowl of the Campfire FBX.
  group.position.y = 0.3;

  const coneSpecs = [
    { r: 0.34, h: 0.95, color: 0xff5a1e, opacity: 0.8 },  // outer orange
    { r: 0.22, h: 0.78, color: 0xff9a2e, opacity: 0.88 }, // mid amber
    { r: 0.12, h: 0.58, color: 0xffe27a, opacity: 0.95 }, // inner yellow
  ];
  const cones: THREE.Mesh[] = [];
  for (const s of coneSpecs) {
    const geom = new THREE.ConeGeometry(s.r, s.h, 7);
    const mat = new THREE.MeshBasicMaterial({
      color: s.color,
      transparent: true,
      opacity: s.opacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    });
    const m = new THREE.Mesh(geom, mat);
    m.position.y = s.h / 2;
    m.renderOrder = 999;
    group.add(m);
    cones.push(m);
  }

  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: campfireGlowTexture(),
    color: 0xff8a3a,
    transparent: true,
    opacity: 0.5,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  }));
  sprite.scale.setScalar(2.4);
  sprite.position.y = 0.45;
  group.add(sprite);

  const light = new THREE.PointLight(0xff7a33, CAMPFIRE_LIGHT_INTENSITY, 9, 2);
  light.position.y = 0.7;
  group.add(light);

  return { group, cones, sprite, light };
}

const PRIMITIVE_COLOR: Record<BuildingType, number> = {
  Tent: 0x7a5a3c,
  Campfire: 0xff8855,
  Palisade: 0x8b6f47,
  Forge: 0x555555,
  Workbench: 0x9a7a4a,
  HuntersLodge: 0x665533,
  StorageOutpost: 0x4a5a3a,
  Tavern: 0x8b3a3a,
  Farm: 0x6a8a3a,
  AutomatedDefenseTurret: 0x444466,
  SpikeTrap: 0x884444,
  Wonder: 0x9966cc,
};

function makePrimitive(type: BuildingType, fp: { cellsX: number; cellsZ: number }): THREE.Object3D {
  const color = PRIMITIVE_COLOR[type] ?? 0x888888;
  const mat = new THREE.MeshLambertMaterial({ color, transparent: true, opacity: 1.0 });
  applyToonShader(mat, { noiseScale: 1.2 });
  if (type === "SpikeTrap") {
    const geom = new THREE.ConeGeometry(0.3, 0.6, 4);
    const m = new THREE.Mesh(geom, mat);
    m.position.y = 0.3;
    return m;
  }
  if (type === "Palisade") {
    const geom = new THREE.BoxGeometry(fp.cellsX, 2.5, fp.cellsZ);
    const m = new THREE.Mesh(geom, mat);
    m.position.y = 1.25;
    return m;
  }
  // Generic prim: tall box matching footprint
  const h = type === "Wonder" ? 6 : 2.5;
  const geom = new THREE.BoxGeometry(fp.cellsX, h, fp.cellsZ);
  const m = new THREE.Mesh(geom, mat);
  m.position.y = h / 2;
  return m;
}

interface BuildingObj {
  id: string;
  type: BuildingType;
  root: THREE.Object3D;
  ringMat: THREE.MeshBasicMaterial;
  ring: THREE.Mesh;
  flame?: CampfireFlame;
}

export function createBuildingRender(
  scene: THREE.Scene,
  assets: AssetBundle,
  sampleHeight: (x: number, z: number) => number,
): BuildingRenderManager {
  const group = new THREE.Group();
  group.name = "buildings";
  scene.add(group);

  const objs = new Map<string, BuildingObj>();
  let elapsed = 0; // seconds; drives campfire flame flicker

  function addBuilding(b: Building): void {
    const type = b.buildingType as BuildingType;
    const def = BUILDING_DEFS[type];
    if (!def) return;
    const fbx = assets.buildings[type];
    let mesh: THREE.Object3D;
    if (fbx) {
      mesh = fbx.clone(true);
      mesh.scale.setScalar(def.scale);
    } else {
      mesh = makePrimitive(type, def.footprint);
    }
    // FBX meshes come with default MeshPhongMaterial (or arrays of them) from the
    // FBX loader. Replace each with a MeshLambertMaterial via `toLambertWithRim`
    // so buildings sit on the world's continuous gradient (terrain + doodads),
    // not the 3-step toon ramp reserved for player characters.
    mesh.traverse((node) => {
      const m = node as THREE.Mesh;
      if (!m.isMesh) return;
      m.castShadow = true;
      m.receiveShadow = true;
      // Bake base-AO on the geometry. FBX clones share geometry across
      // instances of a building type so this runs once per type, not per
      // building.
      bakeBaseAO(m.geometry);
      const src = m.material as THREE.Material | THREE.Material[];
      // Looser noise grain on buildings (1.2 → ~0.83m) reads as wall-stone /
      // plank variation, vs the 2.5 (0.4m) used on doodads which lands closer
      // to bark / leaf grain.
      if (Array.isArray(src)) {
        m.material = src.map((mm) => toLambertWithRim(mm, 1.2));
      } else if (src) {
        m.material = toLambertWithRim(src, 1.2);
      }
    });
    // Construction-progress ring at base — sized to footprint so it scales with
    // the building (a hardcoded 0.4/0.6 looks puny on a 5×4 Wonder).
    const ringMat = new THREE.MeshBasicMaterial({ color: 0xffe066, side: THREE.DoubleSide, transparent: true, opacity: 0.6, depthWrite: false, depthTest: false });
    const fp = def.footprint;
    const halfExtent = Math.max(fp.cellsX, fp.cellsZ) / 2;
    const ringInner = halfExtent * 0.85;
    const ringOuter = halfExtent * 1.05;
    const ringGeom = new THREE.RingGeometry(ringInner, ringOuter, 32);
    ringGeom.rotateX(-Math.PI / 2);
    const ring = new THREE.Mesh(ringGeom, ringMat);
    ring.renderOrder = 998;

    const root = new THREE.Group();
    root.name = `building:${b.id}`;
    root.userData = { id: b.id, type };
    root.add(mesh);
    root.add(ring);

    // Campfires get an animated flame + warm light; hidden until "ready"
    // (reconcile toggles visibility off the construction state).
    let flame: CampfireFlame | undefined;
    if (type === "Campfire") {
      flame = makeCampfireFlame();
      flame.group.visible = false;
      root.add(flame.group);
    }

    root.position.set(b.x, sampleHeight(b.x, b.z), b.z);
    group.add(root);
    objs.set(b.id, { id: b.id, type, root, ringMat, ring, flame });
  }

  function removeBuilding(id: string): void {
    const o = objs.get(id);
    if (!o) return;
    group.remove(o.root);
    o.root.traverse((node) => {
      const m = node as THREE.Mesh;
      if ((m as THREE.Mesh).isMesh) {
        m.geometry?.dispose?.();
        const mat = m.material as THREE.Material | THREE.Material[];
        if (Array.isArray(mat)) mat.forEach((mm) => mm.dispose());
        else mat?.dispose?.();
      }
    });
    objs.delete(id);
  }

  function reconcile(entries: Iterable<[string, Building]>): void {
    const seen = new Set<string>();
    for (const [id, b] of entries) {
      seen.add(id);
      let o = objs.get(id);
      if (!o) { addBuilding(b); o = objs.get(id)!; }
      // Translucency while constructing. Walks every material — array OR single —
      // because FBX-loaded buildings come with phong material arrays (Wonder=10,
      // Tavern=8, Tent=4). `transparent` flips back to false when ready so the
      // depth-sort doesn't get fooled by a fully-opaque-but-transparent flag.
      const constructing = b.state === "constructing";
      const apply = (mm: THREE.Material) => {
        if ("opacity" in mm) {
          (mm as THREE.MeshLambertMaterial).transparent = constructing;
          (mm as THREE.MeshLambertMaterial).opacity = constructing ? 0.55 : 1.0;
          mm.needsUpdate = true;
        }
      };
      o.root.traverse((node) => {
        const m = node as THREE.Mesh;
        if (!(m as THREE.Mesh).isMesh) return;
        const mat = m.material as THREE.Material | THREE.Material[] | undefined;
        if (Array.isArray(mat)) mat.forEach(apply);
        else if (mat) apply(mat);
      });
      // Ring only shows during the wind-up; hide once state flips to "ready".
      o.ring.visible = constructing;
      // Campfire flame + light ignite only once the fire is actually built.
      if (o.flame) o.flame.group.visible = !constructing;
      o.root.position.x = b.x;
      o.root.position.z = b.z;
      o.root.position.y = sampleHeight(b.x, b.z);
    }
    // Remove gone
    for (const id of objs.keys()) {
      if (!seen.has(id)) removeBuilding(id);
    }
  }

  function pickBuilding(raycaster: THREE.Raycaster): string | null {
    const hits = raycaster.intersectObjects(Array.from(objs.values()).map((o) => o.root), true);
    if (hits.length === 0) return null;
    // Walk up to find userData.id
    let n: THREE.Object3D | null = hits[0]!.object;
    while (n && !n.userData?.id) n = n.parent;
    return (n?.userData?.id as string | undefined) ?? null;
  }

  function update(dt: number): void {
    elapsed += dt;
    const t = elapsed;
    for (const o of objs.values()) {
      const f = o.flame;
      if (!f || !f.group.visible) continue;
      // Layered sines → organic, non-periodic-looking flicker.
      const flick = 0.85 + 0.13 * Math.sin(t * 11) + 0.06 * Math.sin(t * 23.3);
      f.cones.forEach((c, i) => {
        const sway = Math.sin(t * (6 + i * 2) + i) * 0.05;
        // Vertical scale carries the flicker; horizontal wobbles gently.
        c.scale.set(1 + sway, flick + sway, 1 + sway);
        c.rotation.z = Math.sin(t * 3 + i) * 0.05;
      });
      (f.sprite.material as THREE.SpriteMaterial).opacity = 0.42 + 0.12 * Math.sin(t * 9);
      f.light.intensity = CAMPFIRE_LIGHT_INTENSITY * (0.82 + 0.12 * Math.sin(t * 13.7) + 0.06 * Math.sin(t * 27.1));
    }
  }

  return { group, reconcile, pickBuilding, update };
}
